import { useEffect, useMemo, useState } from 'react';

export const PAGE_SIZES = [25, 50, 100];

// Shared paging for any list. Returns the visible slice plus the props the
// <Pagination> control needs, so a page only has to render the slice.
//
// The reset-on-shrink guard matters: filtering a list while you're on page 3
// would otherwise leave you staring at an empty table with no obvious cause.
export function usePagination(items, initialSize = PAGE_SIZES[0]) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const visible = useMemo(
    () => items.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [items, page, pageSize]
  );

  return {
    visible,
    controls: {
      page,
      pageCount,
      pageSize,
      total,
      from: total === 0 ? 0 : (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, total),
      onPage: setPage,
      onPageSize: (size) => {
        setPageSize(size);
        setPage(1);
      },
    },
  };
}

export default function Pagination({ page, pageCount, pageSize, total, from, to, onPage, onPageSize }) {
  // Nothing to page through, and no size worth choosing.
  if (total <= PAGE_SIZES[0]) return null;

  return (
    <div className="pagination">
      <span className="pagination-count">
        Showing <strong>{from}–{to}</strong> of {total}
      </span>

      <div className="pagination-controls">
        <button
          className="pagination-button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span className="pagination-page">
          {page} / {pageCount}
        </span>
        <button
          className="pagination-button"
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          ›
        </button>

        <select
          className="pagination-size"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s} per page
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
